import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Button,
  CardHeader,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { createSchedules } from "../../api/schedule.api";
import { sentenceCase, titleCase } from "../../utils/case";

import { getReservations, deleteReservation, resendReservationNotification } from "../../api/reservation.api";

import {
  Eye,
  Edit2,
  XCircle,
  Trash2,
  FileText,
  MessageCircle,
  CloudLightning,
  Mail,
  File,
} from "react-feather";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Breadcrumbs } from "../../AbstractElements";
import { handleWhatsAppNotify } from "../../utils/whatsapp";
import { getCombinedStatus } from "../../utils/rsvnStatus";
import { getTodayRange } from "../../utils/datetime";
import { getVenues } from "../../api/venue.api"

const ReservationMain = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState("0");

  const [data, setData] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [cardPage, setCardPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const calendarRef = useRef(null);
  const [hydrated, setHydrated] = useState(false);

  const [filters, setFilters] = useState({
    range: "all",
    type: "",
  });
  

  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    perPage: 10,
    totalPages: 1,
  });

  const [query, setQuery] = useState({
    page: 1,
    perPage: meta.perPage,
    sortField: "date",
    sortOrder: "desc",
    name: "",
    bookingRef: "",
    venueId: "",
    status: "",
    type: "",
    purpose: "",
    date: "",
  });

  const [draftQuery, setDraftQuery] = useState(query);

  const [venues, setVenues] = useState([]);

  const fetchReservationsData = async ({
    page = 1,
    perPage = 10,
    append = false,
    startDate = null,
    endDate = null,
  } = {}) => {
    try {
      setLoadingMore(true);

      const params = {
        page,
        perPage,
        sortField: query.sortField,
        sortOrder: query.sortOrder,
      };

      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }

      if (query.name) params.name = query.name;
      if (query.bookingRef) params.bookingRef = query.bookingRef;
      if (query.venueId) params.venueId = query.venueId;
      if (query.status) params.status = query.status;
      if (query.type) params.type = query.type;
      if (query.purpose) params.purpose = query.purpose;
      if (query.date) {
        params.startDate = query.date;
        params.endDate = query.date;
      }

      const res = await getReservations(params);

      setMeta(res.meta);
      const newData = res?.data || [];

      setData((prev) => (append ? [...prev, ...newData] : newData));

      if (!startDate) {
        setHasMore(newData.length === perPage);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!hydrated) return;

    fetchReservationsData({
      page: query.page,
      perPage: query.perPage,
    });
  }, [query, hydrated]);

  useEffect(() => {
    const handleScroll = () => {
      if (activeTab !== "0") return;
      if (!hasMore || loadingMore) return;

      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const fullHeight = document.documentElement.scrollHeight;

      // trigger when near bottom (200px threshold)
      if (scrollTop + windowHeight >= fullHeight - 200) {
        const nextPage = cardPage + 1;
        if (nextPage <= meta.totalPages) {
          setCardPage(nextPage);
          fetchReservationsData({ page: nextPage, append: true });
        }
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [activeTab, cardPage, hasMore, loadingMore, meta]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await getVenues({ perPage: 1000 }); // get all
        setVenues(res?.data || []);
      } catch (err) {
        toast.error(err.message);
      }
    };

    fetchVenues();
  }, []);

  // ✅ actions
  const handleView = (row) => {
    navigate(`/reservations/view/${row.id}`);
  };

  const handleEdit = (row) => {
    navigate(`/reservations/edit/${row.id}`);
  };

  const handleDelete = async (row) => {
    const confirmed = window.confirm(t("confirm delete"));
    if (!confirmed) return;

    try {
      setDeletingId(row.id);
      await deleteReservation(row.id);
      toast.success(t("deleted successfully"));
      fetchReservationsData({page: 1});
      setCardPage(1);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreate = () => {
    navigate("/reservations/create");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    // CARD VIEW → reset to page 1
    if (tab === "0") {
      setCardPage(1);
      setHasMore(true);
      handleRangeChange("all");
    }

    // SCHEDULE VIEW → reload using calendar visible range
    if (tab === "2") {
      const calendarApi = calendarRef.current?.getApi();
      if (calendarApi) {
        const start = calendarApi.view.activeStart;
        const end = calendarApi.view.activeEnd;

        fetchReservationsByDateRange(start, end);
      }
    }
  };

  const handleAutoSchedule = async (item) => {
    try {
      const device = item.venue?.devices?.[0];

      if (!device) {
        toast.error("No device found for this venue");
        return;
      }

      const payload = {
        schedules: [
          {
            reservationId: item.id,
            deviceId: device.id,
            action: "on",
            datetime: `${item.date}T${item.startTime}`,
          },
          {
            reservationId: item.id,
            deviceId: device.id,
            action: "off",
            datetime: `${item.date}T${item.endTime}`,
          },
        ],
      };

      await createSchedules(payload);
      handleTabChange(activeTab);

      toast.success("Device schedules created (ON/OFF)");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const fetchReservationsByDateRange = async (start, end) => {
    try {
      setLoadingMore(true);

      const res = await getReservations({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        perPage: 1000, // large enough to cover visible range
      });

      setData(res?.data || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusFromUrl = params.get("status");

    const initial = {
      page: 1,
      perPage: meta.perPage,
      sortField: "date",
      sortOrder: "desc",
      name: "",
      bookingRef: "",
      venueId: "",
      status: statusFromUrl || "",
      type: "",
      purpose: "",
      date: "",
    };

    setQuery(initial);
    setDraftQuery(initial);
    setHydrated(true);
  }, [location.search]);

  const action = (
    <Button color="success" onClick={handleCreate}>
      {t("create").toUpperCase()}
    </Button>
  );

  // ✅ calendar events
  const events = data
    .filter((r) => r.status === "confirmed")
    .map((r) => {

      let color = "#ffc107"; // pending
      if (r.type === "booking") color = "#16c7f9";
      if (r.type === "maintenance") color = "#2c323f";
      if (r.type === "special_event") color = "#d9d9d9";

      return {
        id: r.id,
        title: `${r.venue.name}\n${r.purpose} (${r.status})\n${r.name}\n${r.phoneNo}`,
        start: `${r.date}T${r.startTime}`,
        end: `${r.date}T${r.endTime}`,
        backgroundColor: color,
        textColor: color == "#d9d9d9" ? "#000" : "#fff"
      };
    });

  const handleResend = async (item) => {
    try {
      await resendReservationNotification(item.id);
      toast.success(titleCase(t("email notification resent")));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleViewSlip = (paymentProof) => {
    if (paymentProof) {
      window.open(`https://bfiles.chinfumc.org.my${paymentProof}`, "_blank");
    }
  };

  const handleViewReceipt = (receipt) => {
    if (receipt.documentUrl) {
      window.open(`https://bfiles.chinfumc.org.my${receipt.documentUrl}`, "_blank");
    }
  };

  const ReservationCard = ({ item, onView }) => {
    const formatTime = (time) => {
      if (!time) return "";
      const [hourStr, minute] = time.split(":");
      let hour = parseInt(hourStr, 10);
      const ampm = hour >= 12 ? "PM" : "AM";
      hour = hour % 12 || 12;
      return `${hour}:${minute} ${ampm}`;
    };

    const start = formatTime(item.startTime);
    const end = formatTime(item.endTime);

    let badgeColor = "secondary";
    const status = getCombinedStatus(item);

    if (status === "pending") badgeColor = "warning";
    else if (status === "cancelled") badgeColor = "danger";
    else if (status === "approved") badgeColor = "info";
    else if (status === "paid") badgeColor = "primary";
    else if (status === "confirmed") badgeColor = "success";

    return (
      <Col className="col-xxl-4" md="6">
        <div className={new Date(item.date + " " + item.startTime) < new Date() ? "project-box bg-light text-dark" : "project-box"}>
          {/* Status */}
          <div className="d-flex justify-content-end">
            <Badge color="info">
              {titleCase(item.type?.replace(/_/g, " ") || "")}
            </Badge>
            <Badge color={badgeColor}>
              {titleCase(status)}
            </Badge>
          </div>

          {/* Title */}
          <p className="mt-2">{item.bookingRef || "-"}</p>

          {/* Venue */}
          <div className="mb-2">
            <strong>{item.venue?.name || item.venueId}</strong>
          </div>

          {/* Date & Time */}
          <div className="mb-2">
            <div><strong>{item.date}</strong></div>
            <div>
              <strong>{start} - {end}</strong>
            </div>
          </div>

          {/* Basic Info */}
          <div className="mb-2">
            <div>{item.purpose}</div>
            <div>{item.name}</div>
            <div>{item.phoneNo}</div>
            <div>{item.email}</div>
          </div>

          {/* Actions */}
          <div className="d-flex justify-content-end gap-3 mt-3 align-items-center">
            {/* View */}
            <Eye
              size={18}
              style={{ cursor: "pointer", color: "blue" }}
              title="View"
              onClick={() => onView(item)}
            />

            {/* Transaction Slip */}
            {item.paymentProof && (
              <File
                size={18}
                style={{ cursor: "pointer", color: "purple" }}
                title="View Transaction Slip"
                onClick={() => handleViewSlip(item.paymentProof)}
              />
            )}

            {item.transaction?.receipt && (
              <FileText
                size={18}
                style={{ cursor: "pointer", color: "purple" }}
                title="View Receipt"
                onClick={() => handleViewReceipt(item.transaction?.receipt)}
              />
            )}

            {/* Resend Email */}
            <Mail
              size={18}
              style={{ cursor: "pointer", color: "brown" }}
              title="Resend Email Notification"
              onClick={() => handleResend(item)}
            />

            {/* WhatsApp */}
            {item.phoneNo && item.phoneNo !== "-" && (
              <MessageCircle
                size={18}
                style={{ cursor: "pointer", color: "green" }}
                title="WhatsApp"
                onClick={() => handleWhatsAppNotify(item)}
              />
            )}

            {/* Schedule Device Op */}
            {item.venue?.devices?.length > 0 && item.deviceSchedules.length < 1 && (
              <CloudLightning
                size={18}
                style={{ cursor: "pointer", color: "indigo" }}
                title="Auto Schedule Device"
                onClick={() => handleAutoSchedule(item)}
              />
            )}

            {/* Edit */}
            <Edit2
              size={18}
              style={{ cursor: "pointer", color: "orange" }}
              title="Edit"
              onClick={() => handleEdit(item)}
            />

            {/* Delete */}
            <Trash2
              size={18}
              style={{ cursor: "pointer", color: "red" }}
              title="Delete"
              onClick={() => handleDelete(item)}
            />

          </div>
        </div>
      </Col>
    );
  };

  const handleRangeChange = (range) => {
    setFilters((prev) => ({ ...prev, range }));

    if (range === "today") {
      const { startDate, endDate } = getTodayRange();

      // reset pagination
      setCardPage(1);
      setHasMore(true);

      fetchReservationsData({
        page: 1,
        perPage: meta.perPage,
        startDate,
        endDate,
      });

    } else if (range === "all") {
      setCardPage(1);
      setHasMore(true);

      fetchReservationsData({
        page: 1,
        perPage: meta.perPage,
      });
    }
  };

  return (
    <Fragment>
      <Breadcrumbs
        parent={titleCase(t("reservations"))}
        title={titleCase(t("reservation management"))}
        mainTitle={titleCase(t("reservation management"))}
      />

      <Container fluid>
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                <div className="d-flex justify-content-end align-items-center w-100">
                  {action && <div>{action}</div>}
                </div>
              </CardHeader>
              <CardBody>
                {/* Tabs */}
                <Nav tabs className="border-tab">
                  <NavItem>
                    <NavLink
                      className={activeTab === "0" ? "active bg-primary text-white" : ""}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleTabChange("0")}
                    >
                      {titleCase(t("list view"))}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === "2" ? "active bg-primary text-white" : ""}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleTabChange("2")}
                    >
                      {titleCase(t("schedule view"))}
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="pt-4">
                  {/* CARD VIEW */}
                  <TabPane tabId="0">
                    <div className="mb-3 p-3 border rounded">

                    {/* ===================== */}
                    {/* ROW 1: SORTING */}
                    {/* ===================== */}
                    <Row className="g-2 align-items-end mb-2">

                      <Col md="3">
                        <label className="form-label mb-1">{titleCase(t("sort by"))}</label>
                        <select
                          className="form-control"
                          value={query.sortField}
                          onChange={(e) =>
                            setQuery((p) => ({ ...p, sortField: e.target.value, page: 1 }))
                          }
                        >
                          <option value="date">{titleCase(t("reservation date"))}</option>
                          <option value="createdAt">{titleCase(t("submitted at"))}</option>
                          <option value="name">{titleCase(t("name"))}</option>
                          <option value="bookingRef">{titleCase(t("booking ref"))}</option>
                          <option value="status">{titleCase(t("status"))}</option>
                          <option value="type">{titleCase(t("type"))}</option>
                        </select>
                      </Col>

                      <Col md="3" className="d-flex align-items-end">
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          onClick={() =>
                            setQuery((p) => ({
                              ...p,
                              sortOrder: p.sortOrder === "asc" ? "desc" : "asc",
                            }))
                          }
                        >
                          {t(query.sortOrder).toUpperCase()}
                        </Button>
                      </Col>
                    </Row>

                    <hr />

                    {/* ===================== */}
                    {/* ROW 2: DATE FILTERS */}
                    {/* ===================== */}
                    <Row className="g-2 align-items-end mb-2">

                      <Col md="3">
                        <label className="form-label mb-1">{titleCase(t("reservation date"))}</label>
                        <input
                          type="date"
                          className="form-control"
                          value={query.date}
                          onChange={(e) =>
                            setQuery((p) => ({ ...p, date: e.target.value, page: 1 }))
                          }
                        />
                      </Col>

                      <Col md="3">
                        <label className="form-label mb-1">{titleCase(t("quick range"))}</label>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            outline={filters.range !== "all"}
                            color="primary"
                            onClick={() => handleRangeChange("all")}
                          >
                            {titleCase(t("all"))}
                          </Button>

                          <Button
                            size="sm"
                            outline={filters.range !== "today"}
                            color="primary"
                            onClick={() => handleRangeChange("today")}
                          >
                            {titleCase(t("today"))}
                          </Button>
                        </div>
                      </Col>

                    </Row>

                    <hr />

                    {/* ===================== */}
                    {/* ROW 3: OTHER FILTERS */}
                    {/* ===================== */}
                    <Row className="g-2 align-items-end mb-3">

                      <Col md="4">
                        <label className="form-label mb-1">{titleCase(t("name"))}</label>
                        <input
                          className="form-control"
                          placeholder={sentenceCase(t("search name"))}
                          value={draftQuery.name}
                          onChange={(e) =>
                            setDraftQuery((p) => ({ ...p, name: e.target.value }))
                          }
                        />
                      </Col>

                      <Col md="4">
                        <label className="form-label mb-1">{titleCase(t("booking ref"))}</label>
                        <input
                          className="form-control"
                          placeholder={sentenceCase(t("search booking ref"))}
                          value={draftQuery.bookingRef}
                          onChange={(e) =>
                            setDraftQuery((p) => ({ ...p, bookingRef: e.target.value, page: 1 }))
                          }
                        />
                      </Col>

                      <Col md="4">
                        <label className="form-label mb-1">{titleCase(t("purpose"))}</label>
                        <input
                          className="form-control"
                          placeholder={sentenceCase(t("search purpose"))}
                          value={draftQuery.purpose}
                          onChange={(e) =>
                            setDraftQuery((p) => ({ ...p, purpose: e.target.value, page: 1 }))
                          }
                        />
                      </Col>

                      <Col md="4">
                        <label className="form-label mb-1">{titleCase(t("status"))}</label>
                        <select
                          className="form-control"
                          value={draftQuery.status}
                          onChange={(e) =>
                            setDraftQuery((p) => ({ ...p, status: e.target.value }))
                          }
                        >
                          <option value="">{titleCase(t("all"))}</option>
                          <option value="pending">{titleCase(t("pending"))}</option>
                          <option value="approved">{titleCase(t("approved"))}</option>
                          <option value="paid">{titleCase(t("paid"))}</option>
                          <option value="confirmed">{titleCase(t("confirmed"))}</option>
                        </select>
                      </Col>

                      <Col md="4">
                        <label className="form-label mb-1">{titleCase(t("type"))}</label>
                        <select
                          className="form-control"
                          value={query.type}
                          onChange={(e) =>
                            setQuery((p) => ({ ...p, type: e.target.value, page: 1 }))
                          }
                        >
                          <option value="">{titleCase(t("all"))}</option>
                          <option value="booking">{titleCase(t("booking"))}</option>
                          <option value="maintenance">{titleCase(t("maintenance"))}</option>
                          <option value="special_event">{titleCase(t("special event"))}</option>
                        </select>
                      </Col>

                      <Col md="4">
                        <label className="form-label mb-1">
                          {titleCase(t("venue"))}
                        </label>
                        <select
                          className="form-control"
                          value={draftQuery.venueId}
                          onChange={(e) =>
                            setDraftQuery((p) => ({
                              ...p,
                              venueId: e.target.value,
                            }))
                          }
                        >
                          <option value="">{titleCase(t("all"))}</option>
                          {venues.map((v) => (
                            <option key={v.id} value={v.id}>
                              {v.name}
                            </option>
                          ))}
                        </select>
                      </Col>
                    </Row>

                    <Row>
                      {/* RESET BUTTON */}
                      <Col className="d-flex align-items-end">
                        <Button
                          className="me-2"
                          size="sm"
                          color="primary"
                          outline
                          onClick={() => {
                            setQuery((prev) => ({
                              ...prev,
                              ...draftQuery,
                              page: 1,
                            }));
                            setCardPage(1);
                            setHasMore(true);
                          }}
                        >
                          {titleCase(t("apply"))}
                        </Button>
                        <Button
                          size="sm"
                          color="danger"
                          outline
                          onClick={() => {
                            const reset = {
                              page: 1,
                              perPage: 10,
                              sortField: "date",
                              sortOrder: "desc",
                              name: "",
                              bookingRef: "",
                              venueId: "",
                              status: "",
                              type: "",
                              purpose: "",
                              date: "",
                            };

                            setQuery(reset);
                            setDraftQuery(reset);

                            setFilters({ range: "all", type: "" });
                            setCardPage(1);
                            setHasMore(true);
                          }}
                        >
                          {titleCase(t("reset all"))}
                        </Button>
                      </Col>
                    </Row>

                  </div>
                    <Row>
                      {data.map((item, i) => (
                        <ReservationCard key={i} item={item} onView={handleView} />
                      ))}
                    </Row>
                  </TabPane>

                  {/* SCHEDULE VIEW */}
                  <TabPane tabId="2">
                    <div className="d-flex gap-4 mb-3 flex-wrap align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: "#16c7f9",
                            display: "inline-block",
                            borderRadius: "2px",
                          }}
                        />
                        <span>{titleCase(t("booking"))}</span>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: "#2c323f",
                            display: "inline-block",
                            borderRadius: "2px",
                          }}
                        />
                        <span>{titleCase(t("maintenance"))}</span>
                      </div>

                      <div className="d-flex align-items-center gap-2">
                        <span
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: "#d9d9d9",
                            display: "inline-block",
                            borderRadius: "2px",
                          }}
                        />
                        <span>{titleCase(t("special event"))}</span>
                      </div>
                    </div>
                    <FullCalendar
                      ref={calendarRef}
                      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                      initialView="timeGridWeek"
                      headerToolbar={{
                        left: "prev,next today",
                        center: "title",
                        right: "dayGridMonth,timeGridWeek,timeGridDay",
                      }}
                      events={events}
                      eventContent={(arg) => {
                        const lines = arg.event.title.split('\n')

                        return (
                          <div>
                            {lines.map((line, i) => (
                              <div key={i}>{line}</div>
                            ))}
                          </div>
                        )
                      }}
                      height="auto"
                      eventClick={(info) => {
                        navigate(`/reservations/view/${info.event.id}`);
                      }}
                      datesSet={(arg) => {
                        if (activeTab === "2") {
                          fetchReservationsByDateRange(arg.start, arg.end);
                        }
                      }}
                    />
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
            {activeTab === "0" && loadingMore && (
              <div className="text-center my-3">
                Loading more...
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ReservationMain;